import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { BoxForm, Select, SelectAutocomplete, TextField } from 'components/Form'
import Loading from 'components/Loading'
import Paper from 'components/Paper'
import { emptySelectOptionZod, SelectOption } from 'components/Select'
import { userWorkspaceRoleOptions } from 'features/user'
import { useGetWorkspaceOptions, WorkspaceSlice } from '../hooks/useGetWorkspaceOptions'
import { FieldArrayInput } from './components/form/FieldArrayInput'
import { Schema, UserWorkspaceAttribute } from './schema'

export const UserAddPublisherForm = (): React.JSX.Element => {
  const { t } = useTranslation('features/user')
  const { control, register, watch } = useFormContext<Schema>()
  const { loading, workspaceOptions } = useGetWorkspaceOptions()

  const { userWorkspaceAttributes } = watch()

  if (loading) {
    return <Loading />
  }

  const addNextDisabled = (): boolean =>
    userWorkspaceAttributes.some(
      (userWorkspaceAttribute: UserWorkspaceAttribute) => !userWorkspaceAttribute.workspaceId,
    )

  const workspaceSelect = (index: number): React.JSX.Element => (
    <SelectAutocomplete<Schema, WorkspaceSlice>
      control={control}
      disabledOptions={userWorkspaceAttributes.map(
        ({ workspaceId }: UserWorkspaceAttribute): SelectOption<WorkspaceSlice>['value'] =>
          workspaceId.value,
      )}
      key={`userWorkspaceAttributes.${index}.workspaceId`}
      name={register(`userWorkspaceAttributes.${index}.workspaceId`).name}
      options={workspaceOptions}
    />
  )

  const roleSelect = (index: number): React.JSX.Element => (
    <Select
      control={control}
      key={`userWorkspaceAttributes.${index}.role`}
      name={register(`userWorkspaceAttributes.${index}.role`).name}
      options={userWorkspaceRoleOptions()}
    />
  )

  return (
    <Paper>
      <BoxForm>
        <TextField
          control={control}
          label={t('form.publisher.add.email.label')}
          name='email'
          placeholder={t('form.publisher.add.email.placeholder')}
          sx={{ mb: 2 }}
        />

        <FieldArrayInput<Schema>
          addNextDisabled={addNextDisabled()}
          control={control}
          defaults={{
            role: 'viewer',
            workspaceId: emptySelectOptionZod<WorkspaceSlice>(),
          }}
          inputs={[
            {
              cell: workspaceSelect,
              label: t('form.publisher.common.workspaceId.label'),
            },
            {
              cell: roleSelect,
              label: t('form.publisher.common.role.label'),
            },
          ]}
          name='userWorkspaceAttributes'
        />
      </BoxForm>
    </Paper>
  )
}
